<template>
  <div class="qualification">
    <section class="qualification-card">
      <div class="section-heading">
        <div>
          <h3>Lead qualification</h3>
          <p class="helper">Review the current status or click to change it.</p>
        </div>
        <v-menu v-model="statusMenu" offset-y bottom left :disabled="busy || loading">
          <template v-slot:activator="{ on, attrs }">
            <button type="button" class="status-edit" v-bind="attrs" v-on="on"
              :disabled="busy || loading" aria-label="Edit High Value status">
              <high-value-badge :value="lead.is_high_value" />
              <i class="mdi mdi-pencil-outline" aria-hidden="true"></i>
            </button>
          </template>
          <v-list dense class="status-options">
            <v-subheader>Set qualification</v-subheader>
            <v-list-item :disabled="busy || loading" @click="setManual(true)">
              <v-list-item-title><high-value-badge :value="1" /></v-list-item-title>
              <v-list-item-action v-if="lead.is_high_value == 1"><v-icon small color="green darken-3">mdi-check</v-icon></v-list-item-action>
            </v-list-item>
            <v-list-item :disabled="busy || loading" @click="setManual(false)">
              <v-list-item-title><high-value-badge :value="0" /></v-list-item-title>
              <v-list-item-action v-if="lead.is_high_value == 0"><v-icon small color="deep-orange darken-3">mdi-check</v-icon></v-list-item-action>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <p class="helper status-hint">Manual changes apply to this lead. Re-evaluating replaces the status with the AI decision.</p>
      <div v-if="manualNotice" class="saved-notice" role="status" aria-live="polite">
        <i class="mdi mdi-check-circle-outline" aria-hidden="true"></i> {{ manualNotice }}
      </div>
      <div v-if="result" class="decision" role="status" aria-live="polite">
        <div class="decision-heading"><span class="field-title">Latest evaluation</span><high-value-badge :value="result.is_high_value" /></div>
        <p class="decision-reason"><strong>Decision reason:</strong> {{ result.reason }}</p>
        <p class="helper">{{ result.rules_saved ? 'Lead status and rules saved for future leads.' : 'Lead status saved. Save the rules below to use these instructions for future leads.' }}</p>
      </div>
    </section>
    <lead-conversion-status :lead="lead" @saved="$emit('conversion-saved', $event)" />
    <section class="qualification-card">
      <div class="section-heading">
        <div>
          <h3>Edit prompts for High Value/Survey</h3>
          <p class="helper">High Value and Google surveys have separate prompts. Edit either one below.</p>
          <video-guide ids="qualification" label="How qualification rules work" />
        </div>
        <button v-if="!expanded" type="button" class="qualification-button" :disabled="busy || loading" @click="open">
          <i :class="loading ? 'mdi mdi-loading mdi-spin' : 'mdi mdi-pencil-outline'" aria-hidden="true"></i>
          {{ loading ? 'Loading…' : 'Edit rules' }}
        </button>
        <button v-else type="button" class="text-button" :disabled="busy" @click="expanded = false">
          Hide rules <i class="mdi mdi-chevron-up" aria-hidden="true"></i>
        </button>
      </div>
      <div v-if="expanded" class="rules-editor">
        <div class="section-heading">
          <div>
            <h3>High Value prompts</h3>
            <p class="helper">Choose which leads are a good fit. These prompts only change High Value.</p>
          </div>
        </div>
        <div class="feedback-section">
          <label class="field">
            <span class="field-title">Describe a change</span>
            <span class="helper">Describe what the AI got wrong. It will suggest revised service lists and update this lead’s high-value status. Review the lists below, then save to use them for future leads. This does not submit a Google survey.</span>
            <textarea v-model="feedback" rows="2" placeholder="e.g. Emergency boiler repairs should qualify as high value."
              :disabled="busy" maxlength="4000"></textarea>
          </label>
          <div class="feedback-actions">
            <button type="button" class="qualification-button" :disabled="busy || !feedback.trim()" @click="run('revise')">
              <i class="mdi mdi-auto-fix" aria-hidden="true"></i> Revise &amp; re-evaluate
            </button>
          </div>
        </div>
        <div class="service-fields">
          <label class="field">
            <span class="field-title">What makes a lead high value?</span>
            <span class="helper">Services that qualify a lead as high value.</span>
            <textarea v-model="services" rows="6" :disabled="busy" maxlength="20000" @input="result = null"></textarea>
          </label>
          <label class="field">
            <span class="field-title">What should not count as high value?</span>
            <span class="helper">Exclusions take priority over offered services.</span>
            <textarea v-model="nonServices" rows="6" :disabled="busy" maxlength="20000" @input="result = null"></textarea>
          </label>
        </div>
        <div class="rules-footer">
          <p class="helper">Re-evaluate updates this lead. Save rules to apply them to future leads too.</p>
          <div class="rule-actions">
            <button type="button" class="qualification-button" :disabled="busy" @click="run('preview')">
              <i class="mdi mdi-refresh" aria-hidden="true"></i> Re-evaluate
            </button>
            <button type="button" class="qualification-button primary-action" :disabled="busy" @click="run('save')">Save rules &amp; update lead</button>
          </div>
        </div>
        <survey-rule-prompts />
      </div>
    </section>
    <div v-if="busy" role="status" class="progress-notice"><i class="mdi mdi-loading mdi-spin" aria-hidden="true"></i> {{ progress }}</div>
    <v-alert v-if="error" dense type="error" role="alert">{{ error }}</v-alert>
  </div>
</template>

<script>
import axios from 'axios'
import HighValueBadge from '@/components/common/HighValueBadge.vue'
import LeadConversionStatus from '@/components/common/LeadConversionStatus.vue'
import SurveyRulePrompts from '@/components/common/SurveyRulePrompts.vue'

export default {
  name: 'LeadSurveyInstructions',
  components: { HighValueBadge, LeadConversionStatus, SurveyRulePrompts },
  props: { lead: { type: Object, required: true } },
  data() {
    return { statusMenu: false, expanded: false, loading: false, busy: false, feedback: '', services: '', nonServices: '', version: '', result: null, error: '', progress: '', manualNotice: '' }
  },
  methods: {
    async request(action, isHighValue) {
      const payload = action === 'manual' ? { lead_id: this.lead.id, action, is_high_value: isHighValue } : {
        lead_id: this.lead.id, action, feedback: this.feedback,
        high_value_services: this.services, high_value_non_services: this.nonServices,
        version: this.version
      }
      const response = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/lead-survey-instructions`, payload,
        { headers: { Authorization: 'Bearer ' + this.$store.state.auth.jwt } })
      return response.data
    },
    async setManual(value) {
      if (this.busy || this.loading) return
      this.busy = true
      this.error = ''
      this.manualNotice = ''
      this.statusMenu = false
      this.progress = 'Saving High Value status…'
      try {
        const data = await this.request('manual', value)
        this.result = null
        this.manualNotice = `Saved manually: ${data.is_high_value ? 'High Value' : 'Not High Value'}.`
        this.$emit('saved', data)
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || 'Could not save the status. Please try again.'
      } finally { this.busy = false }
    },
    async open() {
      if (this.expanded || this.loading || this.busy) return
      this.loading = true
      this.error = ''
      try {
        const data = await this.request('load')
        this.services = data.high_value_services
        this.nonServices = data.high_value_non_services
        this.version = data.version
        this.feedback = ''
        this.result = null
        this.expanded = true
      } catch (e) {
        this.error = 'Could not load high-value rules. Please try again.'
      } finally { this.loading = false }
    },
    async run(action) {
      if (this.busy || this.loading) return
      this.busy = true
      this.error = ''
      this.manualNotice = ''
      this.result = null
      this.progress = action === 'revise' ? 'Revising instructions and evaluating this lead…' : 'Evaluating this lead…'
      try {
        const data = await this.request(action)
        this.services = data.high_value_services
        this.nonServices = data.high_value_non_services
        this.result = data
        if (data.saved) {
          if (data.rules_saved) this.version = data.version
          this.$emit('saved', data)
        }
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || 'Could not evaluate this lead. Please try again.'
      } finally { this.busy = false }
    }
  }
}
</script>

<style scoped>
.qualification { display: grid; gap: 12px; color: #374151; }
.qualification-card { border: 1px solid #e5e7eb; border-radius: 10px; background: #fff; overflow: hidden; }
.section-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px; }
.section-heading h3 { margin: 0 0 4px; font-size: 13px; font-weight: 700; color: #111827; }
.qualification .helper { margin: 0; font-size: 12px; line-height: 1.5; color: #6b7280; }
.status-edit { display: inline-flex; align-items: center; gap: 8px; padding: 5px; border: 1px solid transparent; border-radius: 8px; color: #6b7280; flex-shrink: 0; }
.status-edit:hover { border-color: #e5e7eb; background: #f9fafb; color: #374151; }
.qualification .status-hint { padding: 0 16px 16px; }
.status-options { min-width: 220px; }
.saved-notice { padding: 0 16px 16px; font-size: 12px; color: #15803d; }
.decision { border-top: 1px solid #e5e7eb; padding: 16px; background: #fafafa; }
.decision-heading { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
.qualification .decision-reason { font-size: 13px; line-height: 1.6; margin: 10px 0 6px; overflow-wrap: anywhere; }
.rules-editor { border-top: 1px solid #e5e7eb; }
.feedback-section { padding: 16px; background: #fafafa; border-bottom: 1px solid #e5e7eb; }
.field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.field-title { font-size: 12px; font-weight: 700; color: #374151; }
.field textarea { width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; background: #fff; font-size: 13px; line-height: 1.5; color: #374151; resize: vertical; }
.field textarea::placeholder { color: #9ca3af; }
.field textarea:focus { outline: none; border-color: #63c6a8; box-shadow: 0 0 0 2px rgba(99, 198, 168, .15); }
.feedback-actions { display: flex; justify-content: flex-end; margin-top: 10px; }
.service-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; padding: 16px; }
.rules-footer { border-top: 1px solid #e5e7eb; padding: 14px 16px; display: grid; gap: 12px; }
.rule-actions { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 8px; }
.qualification-button { display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 8px 12px; border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; font-size: 12px; font-weight: 700; color: #374151; flex-shrink: 0; }
.qualification-button:hover { background: #f9fafb; border-color: #d1d5db; }
.primary-action { background: #63c6a8; border-color: #63c6a8; color: #fff; }
.primary-action:hover { background: #51b897; border-color: #51b897; }
.text-button { font-size: 12px; font-weight: 700; color: #6b7280; white-space: nowrap; }
.qualification button:focus-visible { outline: 2px solid #276472; outline-offset: 3px; }
.qualification button:disabled, .field textarea:disabled { opacity: .55; cursor: not-allowed; }
.progress-notice { font-size: 12px; color: #6b7280; padding: 0 4px; }
@media (max-width: 600px) {
  .section-heading { flex-wrap: wrap; gap: 12px; }
  .service-fields { grid-template-columns: 1fr; }
  .rule-actions { flex-direction: column; }
  .rule-actions .qualification-button { width: 100%; }
}
</style>
